import { useState, useEffect, useCallback } from "react";
import axios from '../../../api/apiClient';

import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../../common/APICommon';
import { SetAuditTrail, SetAuditTrailBulk } from '../../Reports/AuditTrailReportEmployeeSetup';
import { 
  PDFModal as PDFViewModal, 
  ImageViewModal, 
  ConfirmationModal 
} from '../../../common/Modal';
import { token, domain } from '../../Security';
import { LoadingSpinner } from '../../../common/Loading';
import { fileToBase64 } from '../../../utils/fileUtils';
import { formatDateForInput } from '../../../utils/textUtils';
import { ProgramDetailsTab, } from './Tabs/ProgramDetailsTab';
import { ParticipantsTab } from './Tabs/ParticipantsTab';
// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';

function TrainingAndSeminarProgram({ user, isHideSideMenu }) {
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  var headers = [
    "ID",
    "Title",
    "Date From",
    "Date To",
    "File",
    "Is Active?",
    "Status",
    "Actions"
  ]

  var accessors = [
    "id",
    "title",
    "dateFrom",
    "dateTo",
    "fileName",
    "isActive",
    "status",
    "actions"
  ]

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  // Status update states
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState({
    id: null,
    newStatus: "",
    rowData: null
  });
  const [loadingStatusUpdate, setLoadingStatusUpdate] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: "",
    dateFrom: "",
    dateTo: "",
    file: "",
    fileName: "",
    fileType: "",
    isActive: true,
    fileData: null,
    participants: [] // New field for participants
  });

  const resetLocalVariables = () => {
    setForm({
      title: "",
      dateFrom: "",
      dateTo: "",
      file: "",
      fileName: "",
      fileType: "",
      isActive: true,
      fileData: null,
      participants: []
    });
    setSelectedData(null);
  }

  const [filterData, setFilterData] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [showViewFile, setShowViewFile] = useState(false);
  const [viewFileData, setViewFileData] = useState("");
  const [viewFileType, setViewFileType] = useState("");
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfFileUrl, setPdfFileUrl] = useState(null);

  const apiUrl = domain + "/api/TrainingSeminarProgram";

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setData(response.data);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();

    if (!value) {
      setData(filterData);
      return;
    }

    const res = filterData.filter((f) => {
      const match1 = f.title?.toString().toLowerCase().includes(lowercaseValue);
      const match2 = f.id?.toString().startsWith(lowercaseValue);
      const match3 = f.dateFrom?.toString().toLowerCase().includes(lowercaseValue);
      const match4 = f.dateTo?.toString().toLowerCase().includes(lowercaseValue);
      return match1 || match2 || match3 || match4;
    });

    setData(res);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    let base64String = "";
    let fileToBeSave = "";
    let fileNameToSave = "";
    let fileTypeToSave = "";

    if (form.fileData) {
      try {
        base64String = await fileToBase64(form.fileData);
        fileToBeSave = base64String.split(',')[1];
        fileNameToSave = form.fileData.name;
        fileTypeToSave = form.fileData.type;
      } catch (error) {
        console.error('Error converting file to base64:', error);
        showGeneralMessage("Error processing file", false);
        return;
      }
    }

    // Prepare data for API
    const apiData = {
      title: form.title || "",
      dateFrom: form.dateFrom || "",
      dateTo: form.dateTo || "",
      file: form.fileData ? fileToBeSave : null,
      fileName: form.fileData ? fileNameToSave : null,
      fileType: form.fileData ? fileTypeToSave : null,
      isActive: form.isActive !== undefined ? form.isActive : true,
      participants: (form.participants || []).map(p => ({
        employeePersonalInformationId: p.employeePersonalInformationId,
        status: p.status
      }))
    };
    console.log('Add Submitting data:', apiData);

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      await SetAuditTrail({
        setupArea: "TrainingAndSeminars",
        actionTaken: "insert",
        field: "Add",
        newValue: `${apiData.title}`,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });

      await fetchData();

      showGeneralMessage("Training/Seminar created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error.response || error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to create training/seminar";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) {
      showGeneralMessage("No record selected for update", false);
      return;
    }

    let base64String = "";
    let fileToBeSave = "";
    let fileNameToSave = "";
    let fileTypeToSave = "";

    // Check if file is removed (fileData is null AND form.file is empty string)
    const isFileRemoved = !form.fileData && form.file === "" && form.fileName === "" && form.fileType === "";

    if (form.fileData) {
      try {
        base64String = await fileToBase64(form.fileData);
        fileToBeSave = base64String.split(',')[1];
        fileNameToSave = form.fileData.name;
        fileTypeToSave = form.fileData.type;
      } catch (error) {
        console.error('Error converting file to base64:', error);
        showGeneralMessage("Error processing file", false);
        return;
      }
    }

    const apiData = {
      title: form.title || "",
      dateFrom: form.dateFrom || "",
      dateTo: form.dateTo || "",
      file: isFileRemoved ? null : (form.fileData ? fileToBeSave : (selectedData.file || null)),
      fileName: isFileRemoved ? null : (form.fileData ? fileNameToSave : (selectedData.fileName || null)),
      fileType: isFileRemoved ? null : (form.fileData ? fileTypeToSave : (selectedData.fileType || null)),
      isActive: form.isActive !== undefined ? form.isActive : true,
      participants: form.participants || selectedData.participants || [] // Include participants
    };

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let auditInfo = [];

      if (apiData.title !== selectedData.title) {
        auditInfo.push({
          setupArea: "TrainingAndSeminars",
          actionTaken: "update",
          field: "Title",
          newValue: `${apiData.title}`,
          oldValue: `${selectedData.title}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (apiData.dateFrom !== selectedData.dateFrom) {
        auditInfo.push({
          setupArea: "TrainingAndSeminars",
          actionTaken: "update",
          field: "Date From",
          newValue: `${apiData.dateFrom}`,
          oldValue: `${selectedData.dateFrom}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (apiData.dateTo !== selectedData.dateTo) {
        auditInfo.push({
          setupArea: "TrainingAndSeminars",
          actionTaken: "update",
          field: "Date To",
          newValue: `${apiData.dateTo}`,
          oldValue: `${selectedData.dateTo}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      const hasFileChanged = isFileRemoved || form.fileData || (!form.fileData && !selectedData.file && apiData.file !== null);
      if (hasFileChanged) {
        auditInfo.push({
          setupArea: "TrainingAndSeminars",
          actionTaken: "update",
          field: "File",
          newValue: isFileRemoved ? "File Removed" : (apiData.file ? `Updated: ${apiData.fileName}` : "File Updated"),
          oldValue: selectedData.file ? `${selectedData.fileName}` : "No File",
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (apiData.isActive !== selectedData.isActive) {
        auditInfo.push({
          setupArea: "TrainingAndSeminars",
          actionTaken: "update",
          field: "Is Active?",
          newValue: `${apiData.isActive ? "Active" : "Inactive"}`,
          oldValue: `${selectedData.isActive ? "Active" : "Inactive"}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (auditInfo.length > 0) {
        await SetAuditTrailBulk({ data: auditInfo });
      }

      setShowEditForm(false);
      await fetchData();

      showGeneralMessage("Training/Seminar updated successfully!", true);
      closeEditHandleClick();
    } catch (error) {
      console.error('Error updating data:', error.response || error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to update training/seminar";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  // Status Update Functions
  const handleStatusUpdate = (row, newStatus) => {
    console.log("Change into: ", newStatus);
    console.log("Will call api here");

    // Store the data for the confirmation modal
    setStatusUpdateData({
      id: row.id,
      newStatus: newStatus,
      rowData: row
    });

    // Show confirmation modal
    setShowStatusConfirmation(true);
  };

  const confirmStatusUpdate = async () => {
    const { id, newStatus, rowData } = statusUpdateData;

    try {
      setLoadingStatusUpdate(true);

      console.log("Calling API for ID:", id, "with status:", newStatus);

      // Emulate API call - REPLACE THIS WITH YOUR ACTUAL API CALL
      const response = await axios.put(`${apiUrl}/update-status/${id}`, {
        status: newStatus
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Simulate API delay for testing
      await new Promise(resolve => setTimeout(resolve, 1000));

      // If API call is successful, update the local data
      setData(prevData =>
        prevData.map(item =>
          item.id === id
            ? { ...item, status: newStatus }
            : item
        )
      );

      // Also update filterData to keep it in sync
      setFilterData(prevData =>
        prevData.map(item =>
          item.id === id
            ? { ...item, status: newStatus }
            : item
        )
      );

      // Optional: Audit trail
      await SetAuditTrail({
        setupArea: "TrainingAndSeminars",
        actionTaken: "update",
        field: "Status",
        newValue: `${newStatus}`,
        oldValue: `${rowData?.status || "previous status"}`,
        dateSaved: new Date(),
        savedById: user.id
      });

      showGeneralMessage(`Status updated to ${newStatus} successfully!`, true);
      setShowStatusConfirmation(false);

    } catch (error) {
      console.error('Error updating status:', error);
      showGeneralMessage("Failed to update status", false);
    } finally {
      setShowStatusConfirmation(false);
      setLoadingStatusUpdate(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedData?.id) {
      showGeneralMessage("No record selected for deletion", false);
      return;
    }

    try {
      setLoadingRequest(true);
      await axios.delete(`${apiUrl}/${selectedData.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      await SetAuditTrail({
        setupArea: "TrainingAndSeminars",
        actionTaken: "delete",
        field: "Delete",
        newValue: `${selectedData.title}`,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });

      await fetchData();

      showGeneralMessage("Training/Seminar deleted successfully!", true);
      closeDeleteHandleClick();
    } catch (error) {
      console.error('Error deleting data:', error.response || error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to delete training/seminar";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);
    setForm({
      title: data.title || "",
      dateFrom: data.dateFrom || "",
      dateTo: data.dateTo || "",
      file: data.file || "",
      fileName: data.fileName || "",
      fileType: data.fileType || "",
      isActive: data.isActive !== undefined ? data.isActive : true,
      fileData: null,
      participants: data.participants || [] // Load existing participants
    });
    openEditHandleClick();
  };

  const deleteHandleClick = (data) => {
    setSelectedData(data);
    setShowDeleteForm(true);
  };

  const viewFileHandleClick = (data) => {
    if (!data?.file || !data?.fileType) {
      showGeneralMessage("No file available to view.", false);
      return;
    }

    // If Image → use your existing image modal
    if (data.fileType.startsWith("image/")) {
      setViewFileData(data.file);
      setViewFileType(data.fileType);
      setShowViewFile(true);
      return;
    }

    // If PDF → use your PDFViewModal
    if (data.fileType === "application/pdf") {
      const fileUrl = `data:application/pdf;base64,${data.file}`;
      setPdfFileUrl(fileUrl);
      setShowPdfModal(true);
      return;
    }

    showGeneralMessage("This file type cannot be previewed.", false);
  };

  const openAddHandleClick = () => {
    setShowAddForm(true);
  };

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const openEditHandleClick = () => {
    setShowEditForm(true);
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const openDeleteHandleClick = () => {
    setShowDeleteForm(true);
  };

  const closeDeleteHandleClick = () => {
    setShowDeleteForm(false);
    setSelectedData(null);
  };

  const closeViewFile = () => {
    setShowViewFile(false);
    setViewFileData("");
    setViewFileType("");
  };

  // Custom cell renderer for status dropdown
  const customCellRenderer = (row, accessor) => {
    if(accessor === "dateFrom") {
      return formatDateForInput(row.dateFrom)
    }
    if(accessor === "dateTo") {
      return formatDateForInput(row.dateTo)
    }
    // Add this section for isActive
    if (accessor === "isActive") {
      const isActive = row[accessor];

      if (isActive === true || isActive === "true" || isActive === "True") {
        return (
          <div className="text-green-500 font-semibold">
            Active
          </div>
        );
      } else {
        return (
          <div className="text-red-500 font-semibold">
            Inactive
          </div>
        );
      }
    }

    if (accessor === "status") {
      const status = row.status || 'pending';

      const statusConfig = {
        pending: {
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-300',
          hoverBg: 'hover:bg-yellow-200',
          dotColor: 'bg-yellow-500'
        },
        completed: {
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-300',
          hoverBg: 'hover:bg-green-200',
          dotColor: 'bg-green-500'
        },
        cancelled: {
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          borderColor: 'border-red-300',
          hoverBg: 'hover:bg-red-200',
          dotColor: 'bg-red-500'
        }
      };

      const config = statusConfig[status] || statusConfig.pending;

      return (
        <div className="relative group">
          <select
            value={status}
            onChange={(e) => handleStatusUpdate(row, e.target.value)}
            className={`
          w-full min-w-[120px] 
          ${config.bgColor} ${config.textColor} ${config.borderColor}
          border rounded-lg pl-8 pr-8 py-1.5
          text-sm font-medium
          appearance-none cursor-pointer
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-opacity-50 
          focus:ring-${status === 'pending' ? 'yellow' : status === 'completed' ? 'green' : 'red'}-400
          hover:shadow-sm
        `}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="pending" className="bg-white text-gray-800">Pending</option>
            <option value="completed" className="bg-white text-gray-800">Completed</option>
            <option value="cancelled" className="bg-white text-gray-800">Cancelled</option>
          </select>

          <div className="absolute inset-y-0 right-0 flex items-center pr-1 pointer-events-none">
            <svg className={`w-4 h-4 ${config.textColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <div className={`w-2 h-2 rounded-full ${config.dotColor}`}></div>
          </div>
        </div>
      );
    }

    if (accessor === "fileName") {
      const isImage = row.fileType?.startsWith("image/");
      const hasFile = row.file && row.fileType;

      if (!hasFile) {
        return <span className="text-gray-400">No file</span>;
      }

      if (isImage) {
        const imgSrc = `data:${row.fileType};base64,${row.file}`;

        return (
          <img
            src={imgSrc}
            alt={row.fileName}
            className="h-10 w-10 object-cover rounded cursor-pointer border hover:scale-105 transition-transform"
            onClick={(e) => {
              e.stopPropagation();
              viewFileHandleClick(row);
            }}
          />
        );
      }

      // For non-image files (PDF, etc.)
      return (
        <span
          className="text-blue-600 underline cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            viewFileHandleClick(row);
          }}
        >
          {row.fileName}
        </span>
      );
    }

    if (accessor === "employeeId") {
      const value = row[accessor] ?? "-";
      return (
        <div className="flex items-center">
          <div className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded text-xs font-mono">
            #{value}
          </div>
        </div>
      );
    }

    if (accessor === "department" || accessor === "position") {
      const value = row[accessor] ?? "-";
      return (
        <div className="flex items-center">
          <div className="bg-gray-50 text-gray-700 px-3 py-1.5 rounded-full text-xs border border-gray-200">
            {value}
          </div>
        </div>
      );
    }

    // Default rendering for other cells
    const value = row[accessor] ?? "-";
    const isTrue = ["True", "Yes", "Subscribed", "Active", "Completed"].includes(value);
    const isFalse = ["False", "No", "Unsubscribed", "Inactive", "Unapproved"].includes(value);

    if (isTrue) {
      return (
        <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-medium">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-2"></div>
          {value}
        </div>
      );
    }

    if (isFalse) {
      return (
        <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-red-100 text-red-800 text-sm font-medium">
          <div className="w-1.5 h-1.5 rounded-full bg-red-500 mr-2"></div>
          {value}
        </div>
      );
    }

    return (
      <span className="text-gray-700 px-2">
        {value}
      </span>
    );
  };

  const actions = [
    {
      name: "View",
      tooltip: "view file",
      icon: <VisibilityIcon fontSize="small" />,
      className: "bg-sky-600 hover:bg-sky-700 flex items-center",
      function: viewFileHandleClick,
      visible: true,
    },
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
    {
      name: "Delete",
      tooltip: "delete",
      icon: <DeleteIcon fontSize="small" />,
      className: "bg-red-500 hover:bg-red-600 flex items-center",
      function: deleteHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Talent Management >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Training/Seminar Programs"}</span>
              </div>
              <h3>Training/Seminars Program</h3>
            </div>
            <div className='space-y-4 p-4'>
              <SearchBar handleFilter={handleFilter} />

              <Button icon={<AddIcon />}
                name={"Add Training/Seminar"}
                className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                onClick={openAddHandleClick}
              />
              <CreateTable
                tableName={"TrainingAndSeminars"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
                customCellRenderer={customCellRenderer}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Form */}
      <TrainingSeminarForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleSubmit}
        modalName={"Add Training/Seminar"}
        mode="add"
        showGeneralMessage={showGeneralMessage}
        user={user}
      />

      {/* Edit Form */}
      <TrainingSeminarForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdate}
        modalName={"Update Training/Seminar"}
        mode="edit"
        showGeneralMessage={showGeneralMessage}
        user={user}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showDeleteForm}
        onClose={closeDeleteHandleClick}
        onConfirm={handleDelete}
        title="Delete Training/Seminar"
        message="Are you sure you want to delete this training/seminar?"
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={loadingRequest}
      />

      {/* Status Update Confirmation Modal */}
      <ConfirmationModal
        visible={showStatusConfirmation}
        onClose={() => setShowStatusConfirmation(false)}
        onConfirm={confirmStatusUpdate}
        title="Confirm Status Update"
        message={`Are you sure you want to change the status to "${statusUpdateData.newStatus}"?`}
        confirmText="Yes, Update Status"
        cancelText="Cancel"
        type="info"
        isLoading={loadingStatusUpdate}
      />

      {/* View File Modal - Only for images */}
      <ImageViewModal
        isOpen={showViewFile}
        onClose={closeViewFile}
        fileData={viewFileData}
        fileType={viewFileType}
      />

      {/* Pdf Modal Viewer */}
      <PDFViewModal
        visible={showPdfModal}
        fileUrl={pdfFileUrl}
        onClose={() => {
          setShowPdfModal(false);
          setPdfFileUrl(null);
        }}
      />

      {/* Use the reusable GeneralMessage component */}
      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest || loading || loadingStatusUpdate} />

    </div>
  )
}

// Main Form Component with Tabs
function TrainingSeminarForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  mode = "add",
  showGeneralMessage,
  user
}) {
  const [activeTab, setActiveTab] = useState("program");
  const [errorState, setErrorState] = useState({
    title: false,
    dateFrom: false,
    dateTo: false
  });

  const [employeesList, setEmployeesList] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Fetch employees when participants tab is selected
  useEffect(() => {
    if (isOpen && activeTab === "participants") {
      fetchEmployees();
    }
  }, [isOpen, activeTab]);

  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await axios.get(`${domain}/api/Employee`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setEmployeesList(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      showGeneralMessage("Failed to load employees", false);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();

    // Validation
    const newErrorState = {
      title: !form.title,
      dateFrom: !form.dateFrom,
      dateTo: !form.dateTo
    };

    if (form.dateFrom && form.dateTo) {
      const fromDate = new Date(form.dateFrom);
      const toDate = new Date(form.dateTo);
      if (fromDate > toDate) {
        showGeneralMessage("Date From cannot be later than Date To", false);
        return;
      }
    }

    if (newErrorState.title || newErrorState.dateFrom || newErrorState.dateTo) {
      setErrorState(newErrorState);
      return;
    }

    handleSubmit(e);
    setErrorState({ title: false, dateFrom: false, dateTo: false });
  };

  const handleClose = () => {
    onClose();
    setErrorState({ title: false, dateFrom: false, dateTo: false });
    setActiveTab("program");
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith("image/") || file.type === "application/pdf") {
        setForm(prev => ({
          ...prev,
          fileData: file,
          fileName: file.name,
          fileType: file.type
        }));
      } else {
        showGeneralMessage("Please select an image or PDF file.", false);
      }
    }
  };

  const removeFile = () => {
    setForm(prev => ({
      ...prev,
      fileData: null,
      file: "",
      fileName: "",
      fileType: ""
    }));
  };

  // Participant management functions
  const handleSelectEmployee = (employee) => {
    setForm(prev => {
      const isAlreadySelected = prev.participants.some(p => p.employeeId === employee.id);
      if (isAlreadySelected) {
        showGeneralMessage(`${employee.fullName} is already added`, false);
        return prev;
      }

      const newParticipant = {
        employeePersonalInformationId: employee.id,
        employeeId: employee.employeeId,
        fullName: employee.fullName,
        department: employee.department,
        position: employee.position,
        status: "pending", // pending, confirmed, attended, cancelled
        joinedAt: new Date().toISOString().split('T')[0]
      };

      return {
        ...prev,
        participants: [...prev.participants, newParticipant]
      };
    });
  };

  const handleRemoveParticipant = (employeeId) => {
    setForm(prev => ({
      ...prev,
      participants: prev.participants.filter(p => p.employeeId !== employeeId)
    }));
  };

  const handleSelectMultiple = (selectedEmployees) => {
    setForm(prev => {
      const newParticipants = selectedEmployees.map(employee => ({
        employeePersonalInformationId: employee.id,
        employeeId: employee.employeeId,
        fullName: employee.fullName,
        department: employee.employeeInformation.departmentDescription,
        position: employee.employeeInformation.positionDescription,
        status: "pending",
        joinedAt: new Date().toISOString().split('T')[0]
      }));

      // Filter out duplicates
      const uniqueParticipants = newParticipants.filter(newPart =>
        !prev.participants.some(existing => existing.employeeId === newPart.employeeId)
      );

      return {
        ...prev,
        participants: [...prev.participants, ...uniqueParticipants]
      };
    });
  };

  const handleParticipantStatusChange = (employeeId, newStatus) => {
    console.log("changeed: ", employeeId, newStatus) // add
    setForm(prev => ({
      ...prev,
      participants: prev.participants.map(p =>
        p.employeeId === employeeId ? { ...p, status: newStatus } : p
      )
    }));
  };

  if (!isOpen) return null;

  const isEditMode = mode === "edit";
  const hasFile = form.fileData || form.file;
  const filePreview = form.fileData
    ? (form.fileData.type.startsWith("image/") ? URL.createObjectURL(form.fileData) : null)
    : (form.file && form.fileType && form.fileType.startsWith("image/") ? `data:image/jpeg;base64,${form.file}` : null);

  const getFileIcon = () => {
    if (form.fileData) {
      if (form.fileData.type.startsWith("image/")) return "🖼️";
      if (form.fileData.type === "application/pdf") return "📄";
    } else if (form.fileType) {
      if (form.fileType.startsWith("image/")) return "🖼️";
      if (form.fileType === "application/pdf") return "📄";
    }
    return "📎";
  };

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3 text-gray-700"
      >
        <div className="relative flex h-auto max-h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:w-[90%] lg:w-[80%] xl:w-[70%]">

          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
            <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>
            <button
              onClick={handleClose}
              className="border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-700"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 bg-white pt-3">
            <div className="flex space-x-1 px-6">
              <button
                onClick={() => setActiveTab("program")}
                className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${activeTab === "program"
                  ? "border-blue-500 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Program Details
              </button>
              <button
                onClick={() => setActiveTab("participants")}
                className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${activeTab === "participants"
                  ? "border-blue-500 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Participants ({form.participants?.length || 0})
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === "program" && (
              <ProgramDetailsTab
                form={form}
                errorState={errorState}
                handleInputChange={handleInputChange}
                handleFileChange={handleFileChange}
                removeFile={removeFile}
                filePreview={filePreview}
                hasFile={hasFile}
                getFileIcon={getFileIcon}
              />
            )}

            {activeTab === "participants" && (
              <ParticipantsTab
                form={form}
                employeesList={employeesList}
                loadingEmployees={loadingEmployees}
                handleSelectEmployee={handleSelectEmployee}
                handleRemoveParticipant={handleRemoveParticipant}
                handleSelectMultiple={handleSelectMultiple}
                handleParticipantStatusChange={handleParticipantStatusChange}
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between space-x-2 rounded-b-lg border-t border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center text-sm text-gray-600">
              {activeTab === "participants" && (
                <span>
                  {form.participants?.length || 0} participant(s) selected
                </span>
              )}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={handleClose}
                className="cursor-pointer rounded border-none bg-gray-400 px-4 py-2 text-white transition-colors hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                className="cursor-pointer rounded border-none bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
              >
                {isEditMode ? "Update" : "Save"}
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

export default TrainingAndSeminarProgram;